import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useCurrentUser } from "@/lib/auth";
import {
  bootstrapDesignPreferenceFromServer,
  NEW_DESIGN_CHANGE_EVENT,
  readDesignPreferenceLocal,
  saveDesignPreference,
} from "@/lib/designPreference";
import { maybeRedirectReactToLegacyOnLoad } from "@/lib/designPreview";

interface DesignPreferenceContextValue {
  newDesign: boolean;
  setNewDesign: (enabled: boolean) => void;
  loading: boolean;
}

const DesignPreferenceContext = createContext<DesignPreferenceContextValue | null>(null);

export function DesignPreferenceProvider({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser();
  const [newDesign, setNewDesignState] = useState(() => readDesignPreferenceLocal());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void bootstrapDesignPreferenceFromServer()
      .then((enabled) => {
        if (cancelled) return;
        setNewDesignState(enabled);
        if (!enabled) {
          maybeRedirectReactToLegacyOnLoad(
            window.location.pathname,
            window.location.search,
            window.location.hash,
          );
        }
      })
      .catch(() => {
        if (cancelled) return;
        setNewDesignState(readDesignPreferenceLocal());
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    const onChange = (event: Event) => {
      const detail = (event as CustomEvent<{ enabled?: boolean }>).detail;
      if (typeof detail?.enabled === "boolean") {
        setNewDesignState(detail.enabled);
        return;
      }
      setNewDesignState(readDesignPreferenceLocal());
    };

    window.addEventListener(NEW_DESIGN_CHANGE_EVENT, onChange);
    window.addEventListener("storage", onChange);
    return () => {
      window.removeEventListener(NEW_DESIGN_CHANGE_EVENT, onChange);
      window.removeEventListener("storage", onChange);
    };
  }, []);

  const setNewDesign = useCallback((next: boolean) => {
    setNewDesignState(next);
    void saveDesignPreference(next);
  }, []);

  const value = useMemo(
    () => ({ newDesign, setNewDesign, loading }),
    [newDesign, setNewDesign, loading],
  );

  return (
    <DesignPreferenceContext.Provider value={value}>{children}</DesignPreferenceContext.Provider>
  );
}

export function useDesignPreference(): DesignPreferenceContextValue {
  const ctx = useContext(DesignPreferenceContext);
  if (!ctx) {
    return {
      newDesign: readDesignPreferenceLocal(),
      setNewDesign: (next) => {
        void saveDesignPreference(next);
      },
      loading: false,
    };
  }
  return ctx;
}
